<script setup lang="ts">
import { Application, Container, Graphics } from 'pixi.js';
import { markRaw, onMounted, onUnmounted, ref, shallowRef, watch } from 'vue';

import { ClientWorld } from '../ClientWorld';
import { ClientServiceContainer } from '../services/ClientServiceContainer';

const props = defineProps<{
  services: ClientServiceContainer;
  world: ClientWorld;
  entityId: number;
  size?: number; // Size of the canvas in pixels
  zoom?: number;
  headMode?: boolean; // Render only the head region (top 16x16 of sprite)
}>();

const emit = defineEmits<{
  (e: 'tick', deltaTime: number): void;
}>();

const pixiContainer = ref<HTMLDivElement | null>(null);
const app = shallowRef<Application | null>(null);
const representation = shallowRef<Container | null>(null);

// Used to prevent initialization race conditions if the component
// is quickly mounted and unmounted before the async initPixi completes.
let isUnmounted = false;
let mask: Graphics | null = null;
let lastAnimationFrame: number | undefined = undefined;
let lastRotation: number | undefined = undefined;

const initPixi = async () => {
  if (!pixiContainer.value || isUnmounted) return;

  const canvasSize = props.size || 128;
  const zoom = props.zoom || 2;

  const pixiApp = new Application();
  try {
    await pixiApp.init({
      width: canvasSize,
      height: canvasSize,
      backgroundAlpha: 0, // Transparent background
      roundPixels: true,
      antialias: false,
    });

    if (isUnmounted) {
      pixiApp.destroy({ removeView: true });
      return;
    }

    app.value = markRaw(pixiApp);
    pixiContainer.value.appendChild(pixiApp.canvas);

    const container = new Container();
    // Center the container in the app
    container.position.set(canvasSize / 2, canvasSize); // Grounded at bottom center
    container.scale.set(zoom);
    container.label = `inspector_representation_${props.entityId}`;
    pixiApp.stage.addChild(container);
    representation.value = markRaw(container);

    // Apply mask if in head mode to ensure clean cropping
    if (props.headMode) {
      mask = new Graphics();
      mask.rect(0, 0, canvasSize, canvasSize).fill(0xffffff);
      pixiApp.stage.addChild(mask);
      pixiApp.stage.mask = mask;
    }

    // Add a simple ticker to update the representation
    pixiApp.ticker.add((ticker) => {
      // Safety check for unmounted state or missing props
      if (isUnmounted || !representation.value || props.entityId === undefined) return;
      
      // Permanent safeguard: Ensure the inspector doesn't accidentally operate 
      // on game-world containers due to reactive object sharing.
      if (representation.value.label && representation.value.label.startsWith('entity_')) {
        console.error(`[EntityRepresentation] LEAK DETECTED: Operating on game container ${representation.value.label} for entity ${props.entityId}`);
      }

      try {
        // Sync the container structure and sprites with the current entity state.
        // We pass the raw container to ensure no reactivity overhead or interference.
        props.services.representation.reconcile(
          representation.value,
          props.world,
          props.entityId,
        );

        // Apply additional transforms from the world if available (e.g., for "live" mirroring)
        const transform = props.world.getComponent(props.entityId, 'Transform');
        const animation = props.world.getComponent(props.entityId, 'Animation');

        if (transform && representation.value) {
          const rotation = transform.rotation;
          const z = props.zoom || 2;
          const scaleX = z * transform.scaleX;
          const scaleY = z * transform.scaleY;

          // Only update rotation if it changed
          if (representation.value.rotation !== rotation) {
            representation.value.rotation = rotation;
          }

          // Only update scale if it changed
          if (representation.value.scale.x !== scaleX || representation.value.scale.y !== scaleY) {
            representation.value.scale.set(scaleX, scaleY);
          }

          // Center the entity mathematically by calculating its visual center
          // and using that as the pivot point.
          // Optimization: Only recalculate bounds if the animation frame or rotation changed
          const currentFrame = animation?.animationFrame;
          if (currentFrame !== lastAnimationFrame || rotation !== lastRotation) {
            // Reset pivot before calculating bounds to ensure a clean local coordinate space
            representation.value.pivot.set(0, 0);
            
            const bounds = representation.value.getLocalBounds();
            representation.value.pivot.set(
              bounds.x + bounds.width / 2,
              bounds.y + bounds.height / 2,
            );
            lastAnimationFrame = currentFrame;
            lastRotation = rotation;
          }

          const currentCanvasSize = props.size || 128;
          representation.value.position.set(currentCanvasSize / 2, currentCanvasSize / 2);
        } else if (representation.value) {
          // Reset orientation and scale to defaults for mock world / non-transformed entities
          // This prevents "live" mirroring from persisting if the component is reused.
          if (representation.value.rotation !== 0) {
            representation.value.rotation = 0;
          }
          const z = props.zoom || 2;
          if (representation.value.scale.x !== z || representation.value.scale.y !== z) {
            representation.value.scale.set(z, z);
          }

          // Default: feet-grounded bottom-center, used for animated idle portraits in mock world
          const currentCanvasSize = props.size || 128;
          representation.value.pivot.set(0, 0);
          representation.value.position.set(currentCanvasSize / 2, currentCanvasSize);
        }

        // Apply head-mode positioning if enabled
        // This overrides any transform-based positioning to ensure the head remains centered
        if (props.headMode && representation.value) {
          const s = props.size || 128;
          // Standard head is top 16x16 of a 32px tall sprite.
          // Center of head is approx at y = -24 (relative to feet at 0, assuming 0.5/1.0 anchor)
          representation.value.pivot.set(0, -24);
          representation.value.position.set(s / 2, s / 2);
        }

        emit('tick', ticker.deltaTime / 60);
      } catch (e) {
        // Log but don't crash the renderer if reconcile fails (e.g. entity destroyed)
        console.warn('Reconcile failed in EntityRepresentation', e);
      }
    });
  } catch (e) {
    console.error('Failed to initialize Pixi application', e);
    pixiApp.destroy({ removeView: true });
  }
};

onMounted(() => {
  initPixi();
});

onUnmounted(() => {
  isUnmounted = true;
  if (mask) {
    mask.destroy();
    mask = null;
  }
  if (app.value) {
    // Pixi 8 cleanup pattern
    app.value.ticker.stop();
    app.value.destroy({ removeView: true });
    app.value = null;
    representation.value = null;
  }
});

// Re-center if size changes
watch(() => props.size, (newSize) => {
  if (app.value && app.value.renderer && representation.value) {
    const s = newSize || 128;
    try {
      app.value.renderer.resize(s, s);

      // Re-apply positioning logic based on whether we have a transform
      const transform = props.world.getComponent(props.entityId, 'Transform');
      if (transform) {
        representation.value.position.set(s / 2, s / 2);
      } else {
        representation.value.position.set(s / 2, s);
      }
    } catch {
      // Ignore errors during resize as it might happen mid-destruction
    }
  }
});

// Update scale if zoom changes
watch(() => props.zoom, (newZoom) => {
  if (representation.value) {
    const z = newZoom || 2;
    representation.value.scale.set(z);
  }
});

// Update label if entityId changes
watch(() => props.entityId, (newId) => {
  if (representation.value) {
    representation.value.label = `inspector_representation_${newId}`;
  }
});
</script>

<template>
  <div
    ref="pixiContainer"
    class="entity-representation"
  />
</template>

<style scoped>
.entity-representation {
  display: inline-block;
  line-height: 0;
  image-rendering: pixelated;
}

.entity-representation :deep(canvas) {
  display: block;
}
</style>
